import { useSubpantalla } from '../ui/navegacionReact';
import { useEffect, useState } from 'react';
import { LifeBuoy, Loader2, MessageSquare, CheckCircle2, ChevronLeft } from 'lucide-react';
import { api, ErrorApi } from '../api';
import Ayuda from '../ui/Ayuda';
import EstadoVacio from '../ui/EstadoVacio';
import TiempoRelativo from '../ui/TiempoRelativo';
import Reportar from './Reportar';

/* =====================================================================
   Mis reportes · DIRECTRIZ-TICKETS F2
   =====================================================================
   El solicitante ve en qué va lo suyo, responde y confirma cuando quedó
   resuelto. No ve notas internas ni tickets de otros: eso lo garantiza el
   backend (RLS + «ver mis reportes»). Aquí solo se pinta lo que le toca.
   ===================================================================== */

// Estado del ticket en palabras y color. verde=bien, amarillo=atención,
// azul=en curso, gris=cerrado (consistencia de colores de estado).
const ESTADO = {
  NUEVO: { texto: 'Nuevo', clase: 'bg-blue-100 text-blue-800' },
  ASIGNADO: { texto: 'Asignado', clase: 'bg-blue-100 text-blue-800' },
  EN_PROCESO: { texto: 'En proceso', clase: 'bg-blue-100 text-blue-800' },
  EN_ESPERA: { texto: 'En espera', clase: 'bg-amber-100 text-amber-800' },
  RESUELTO: { texto: 'Resuelto', clase: 'bg-green-100 text-green-800' },
  CERRADO: { texto: 'Cerrado', clase: 'bg-gray-200 text-gray-700' },
  REABIERTO: { texto: 'Reabierto', clase: 'bg-amber-100 text-amber-800' },
};

function Chip({ estado }) {
  const e = ESTADO[estado] || { texto: estado, clase: 'bg-gray-200 text-gray-700' };
  return <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${e.clase}`}>{e.texto}</span>;
}

export default function MisReportes() {
  const [lista, setLista] = useState(null);
  const [error, setError] = useState('');
  const [abierto, setAbierto] = useState(null);   // detalle de un ticket
  useSubpantalla('reporte', !!abierto, () => setAbierto(null), { valor: abierto, abrir: setAbierto });
  const [reportando, setReportando] = useState(false);

  function recargar() {
    api.get('/tickets?solo_mios=true')
      .then((l) => { setLista(l); setError(''); })
      .catch((e) => setError(e instanceof ErrorApi ? e.message : 'No se pudo cargar tus reportes.'));
  }
  useEffect(recargar, []);

  if (abierto) {
    return <Detalle id={abierto} onVolver={() => { setAbierto(null); recargar(); }} />;
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4 p-4">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">Mis reportes</h1>
        <p className="text-gray-600">En qué va lo que reportaste. Responde o confirma cuando quede resuelto.</p>
        <Ayuda id="mis_reportes.pantalla" />
      </header>

      <button
        type="button"
        onClick={() => setReportando(true)}
        className="flex min-h-[48px] items-center justify-center gap-2 rounded-xl
                   bg-[var(--color-marca)] px-4 font-semibold text-white hover:opacity-95"
      >
        <LifeBuoy className="h-5 w-5" aria-hidden="true" /> Reportar un problema
      </button>

      {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}

      {lista === null && !error && (
        <p className="flex items-center gap-2 py-8 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Cargando tus reportes…
        </p>
      )}

      {lista !== null && lista.length === 0 && (
        <EstadoVacio
          icono={LifeBuoy}
          titulo="Todavía no has reportado nada"
          descripcion="Cuando algo ande mal —una impresora, una terminal, una puerta— repórtalo aquí y podrás seguir en qué va."
        />
      )}

      {lista !== null && lista.length > 0 && (
        <ul className="flex flex-col gap-2">
          {lista.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => setAbierto(t.id)}
                className="flex w-full items-center justify-between gap-3 rounded-xl border
                           border-gray-200 p-3 text-left hover:bg-gray-50"
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium text-gray-900">{t.titulo}</span>
                  <span className="block text-xs text-gray-500">
                    {t.folio} · <TiempoRelativo valor={t.creado_en} />
                  </span>
                </span>
                <Chip estado={t.estado} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {reportando && (
        <Reportar onCerrar={() => setReportando(false)} onEnviado={() => recargar()} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// El detalle de un reporte: la conversación pública, responder y confirmar
// ---------------------------------------------------------------------
function Detalle({ id, onVolver }) {
  const [t, setT] = useState(null);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [ocupado, setOcupado] = useState(false);

  function recargar() {
    api.get(`/tickets/${id}`)
      .then((d) => { setT(d); setError(''); })
      .catch((e) => setError(e instanceof ErrorApi ? e.message : 'No se pudo abrir el reporte.'));
  }
  useEffect(recargar, [id]);

  async function responder() {
    if (!mensaje.trim() || ocupado) return;
    setOcupado(true);
    try {
      await api.post(`/tickets/${id}/eventos`, { mensaje: mensaje.trim() });
      setMensaje('');
      recargar();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo enviar tu mensaje.');
    } finally { setOcupado(false); }
  }

  async function confirmar() {
    if (ocupado) return;
    setOcupado(true);
    try { await api.post(`/tickets/${id}/confirmar`, {}); recargar(); }
    catch (e) { setError(e instanceof ErrorApi ? e.message : 'No se pudo confirmar.'); }
    finally { setOcupado(false); }
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4 p-4">
      <button type="button" onClick={onVolver}
              className="flex items-center gap-1 self-start text-sm text-gray-600 hover:text-gray-900">
        <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Volver a mis reportes
      </button>

      {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}
      {!t && !error && (
        <p className="flex items-center gap-2 py-8 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Abriendo el reporte…
        </p>
      )}

      {t && (
        <>
          <header>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-semibold text-gray-900">{t.titulo}</h1>
              <Chip estado={t.estado} />
            </div>
            <p className="text-xs text-gray-500">{t.folio}</p>
          </header>

          {t.estado === 'RESUELTO' && (
            <div className="rounded-xl border border-green-200 bg-green-50 p-3">
              <p className="text-sm text-green-900">
                Quien atiende marcó tu reporte como resuelto. Si ya quedó bien, confírmalo.
              </p>
              <Ayuda id="mis_reportes.confirmar" />
              <button
                type="button" onClick={confirmar} disabled={ocupado}
                className="mt-2 flex min-h-[44px] items-center justify-center gap-2 rounded-xl
                           bg-green-600 px-4 font-semibold text-white disabled:opacity-50"
              >
                <CheckCircle2 className="h-5 w-5" aria-hidden="true" /> Sí, quedó resuelto
              </button>
            </div>
          )}

          <section>
            <h2 className="mb-2 flex items-center gap-2 font-medium text-gray-900">
              <MessageSquare className="h-4 w-4" aria-hidden="true" /> Conversación
            </h2>
            <ul className="flex flex-col gap-2">
              {t.eventos.filter((e) => e.mensaje).map((e) => (
                <li key={e.id} className="rounded-lg bg-gray-50 p-2 text-sm">
                  <span className="text-gray-800">{e.mensaje}</span>
                  <span className="ml-2 text-xs text-gray-400"><TiempoRelativo valor={e.creado_en} /></span>
                </li>
              ))}
            </ul>
          </section>

          {t.estado !== 'CERRADO' && (
            <div className="flex flex-col gap-2">
              <textarea
                value={mensaje} onChange={(e) => setMensaje(e.target.value)} rows={2}
                placeholder="Escribe una respuesta para quien atiende…"
                className="w-full rounded-xl border border-gray-300 p-3 text-base
                           focus:border-[var(--color-marca)] focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)]"
              />
              <button
                type="button" onClick={responder} disabled={!mensaje.trim() || ocupado}
                className="min-h-[44px] rounded-xl bg-[var(--color-marca)] px-4 font-semibold
                           text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                Enviar respuesta
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
